import { useQuery, useMutation, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "./api";
import { ThreadMessage } from "./websocket";
import type { StoredCheckin, PainArea } from "./checkin";
import type { RecentSessionSets } from "./exerciseHistory";

/**
 * One place for every server-state key and fetcher. Query keys are declared here rather than inline at
 * call sites so an invalidation after a mutation can't silently miss a cache entry because two files
 * spelled the same key differently.
 */
export const queryKeys = {
  coachOverview: ["coach", "overview"] as const,
  clients: ["clients"] as const,
  invites: ["invites"] as const,
  library: (search: string, tag: string) => ["library", search, tag] as const,
  libraryEntry: (entryId: string) => ["library", "entry", entryId] as const,
  planHistory: (clientId: string) => ["plans", "history", clientId] as const,
  currentPlan: (clientId: string) => ["plans", "current", clientId] as const,
  goals: (clientId: string) => ["goals", clientId] as const,
  clientNotes: (clientId: string) => ["clientNotes", clientId] as const,
  sessions: (clientId: string) => ["sessions", clientId] as const,
  sessionDraft: (clientId: string) => ["sessionDraft", clientId] as const,
  exerciseHistory: (clientId: string) => ["exerciseHistory", clientId] as const,
  sessionExercises: (sessionId: string) => ["sessionExercises", sessionId] as const,
  sessionDetail: (sessionId: string) => ["sessionDetail", sessionId] as const,
  threads: (clientId: string) => ["threads", clientId] as const,
  messages: (threadId: string) => ["messages", threadId] as const,
  consent: ["consent"] as const,
  accountStatus: ["accountStatus"] as const,
  billingStatus: ["billing", "status"] as const,
  unreadMessages: ["messages", "unread"] as const,
  familyBilling: (guardianAccountId: string) => ["billing", "family", guardianAccountId] as const,
};

export interface ClientListItem {
  id: string;
  name: string;
  is_minor: boolean;
  guardian_email: string | null;
  has_published_plan: boolean;
  /** Collected on the invite. Null for clients created before these were carried through, or added another way. */
  age: number | null;
  level: string | null;
  /**
   * `invited` until the family finishes signing up. The client exists from the moment the coach sends
   * the invite so a plan can be built in the meantime, so the roster contains people who have not
   * arrived yet and the UI has to say which is which.
   */
  status: "invited" | "active" | "inactive";
  /** The invite that created this client -- what Resend and Cancel act on. Null when added directly. */
  invite_id: string | null;
}

export interface FamilyClientListItem {
  id: string;
  name: string;
  is_minor: boolean;
  editable: boolean;
  /** Null until a login is created for this minor -- what decides whether the create-login form shows. */
  skater_login_id: string | null;
  /**
   * What the guardian actually needs to see about the login (PRD §11.2): the username the child types,
   * and when it was created or last reset. The sub above is internal and is never displayed.
   */
  skater_login_username: string | null;
  skater_login_created_at: string | null;
  skater_login_password_reset_at: string | null;
}

export interface InviteListItem {
  id: string;
  invite_type: "minor" | "adult";
  contact_name: string;
  contact_email: string;
  athlete_name: string;
  status: "pending" | "accepted" | "cancelled";
  /**
   * Non-null once SES reported the invite email hard-bounced or was marked as spam. Null covers both
   * "delivered" and "nothing heard yet" -- only failures are reported, so this can never confirm arrival.
   */
  delivery_status: "bounced" | "complained" | null;
  delivery_failed_at: string | null;
}

export function useClients<T = ClientListItem[]>(): UseQueryResult<T> {
  return useQuery({ queryKey: queryKeys.clients, queryFn: () => apiGet<T>("/clients") });
}

export interface CoachOverviewClient extends ClientListItem {
  billing_status: "pending" | "active" | "past_due" | "cancelled" | null;
  /** When this family's subscription ends, if they have asked to cancel. */
  cancel_at?: string | null;
  /** A stop the coach set on this athlete, waiting for the paid-for period to end. */
  pending_inactive_at?: string | null;
  /** Which of the coach's plans this athlete is on. Absent on athletes who predate the catalogue. */
  subscription_plan_id?: string | null;
  current_plan: { id: string; title: string; elements: unknown; status: string; published_at: string | null } | null;
  sessions: SessionHistoryRow[];
  /** Reports of something wrong that the coach hasn't marked as seen. Absent on an older response. */
  open_flags?: IssueFlag[];
  /** The family's latest plan request, named and priced. Absent on an older response. */
  plan_request?: (PlanRequestSummary & { plan_price_cents: number | null }) | null;
  /** Self-serve switches and cancellations the coach hasn't marked as seen. Absent on an older response. */
  billing_notices?: BillingNotice[];
}

/**
 * An athlete saying something is wrong, delivered on its own rather than buried in a session record.
 *
 * The old behaviour: a flag was an attribute of a *completed* session, so it reached the coach only if
 * the athlete pushed on through every remaining exercise and rated the workout at the end. The more
 * serious the problem, the less likely that was (UX review F72).
 */
export interface IssueFlag {
  id: string;
  client_id: string;
  exercise_name: string | null;
  set_index: number | null;
  note: string | null;
  reported_by_role: "guardian" | "athlete";
  /** True when the athlete stopped the workout at that point -- a much stronger signal than a note. */
  session_ended: boolean;
  /**
   * "checkin" when it came from "Is anything hurting?" before a workout. Then `session_ended` means
   * they chose not to train. Absent on an older response, where every flag came from a workout.
   */
  source?: "session" | "checkin";
  pain_areas?: PainArea[];
  created_at: string;
  acknowledged_at: string | null;
}

/** Takes a report off the coach's "needs attention" list once they have actually seen it. */
export function useAcknowledgeFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, flagId }: { clientId: string; flagId: string }) =>
      apiPost(`/clients/${clientId}/flags/${flagId}/acknowledge`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.coachOverview }),
  });
}

/**
 * The coach answering a family's plan request (27 Sep debug notes item 12). Billing summaries are
 * invalidated too, since an approval can move the athlete onto a new plan.
 */
export function useDecidePlanRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, outcome }: { clientId: string; outcome: "approve" | "decline" }) =>
      apiPost(`/billing/athletes/${clientId}/plan-request/${outcome}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.coachOverview });
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}

export function useMarkBillingNoticeSeen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, noticeId }: { clientId: string; noticeId: string }) =>
      apiPost(`/billing/athletes/${clientId}/notices/${noticeId}/seen`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.coachOverview }),
  });
}

export interface CoachOverview {
  clients: CoachOverviewClient[];
  invites: InviteListItem[];
}

/**
 * The coach's entire working set, in one request.
 *
 * This replaces fetching clients, invites, plans and sessions per screen. That split wasn't a decision
 * about data volume -- it was a straight port of the old SQL endpoints -- and at one coach with a roster
 * in the tens there is no reason for it. Two of those calls were outright waste: clients and invites live
 * in the same DynamoDB partition and come back from a single Query.
 *
 * One query key means one cache entry, so a single refetch (on focus, or after any mutation) brings every
 * coach screen up to date at once, rather than each page invalidating its own slice and hoping the others
 * notice.
 */
export function useCoachOverview(): UseQueryResult<CoachOverview> {
  return useQuery({ queryKey: queryKeys.coachOverview, queryFn: () => apiGet<CoachOverview>("/coach/overview") });
}

/** The one client, out of the overview already in memory -- no per-client request. */
export function useCoachClient(clientId: string | undefined): {
  client: CoachOverviewClient | undefined;
  query: UseQueryResult<CoachOverview>;
} {
  const query = useCoachOverview();
  // `clients?.` and not just `data?.`: the optional chain stopped one level short, so any response
  // without a `clients` key -- an older shape, a partial cache write -- threw while rendering rather
  // than reading as "no client found".
  return { client: query.data?.clients?.find((c) => c.id === clientId), query };
}

export function useInvites(): UseQueryResult<InviteListItem[]> {
  return useQuery({ queryKey: queryKeys.invites, queryFn: () => apiGet<InviteListItem[]>("/invites") });
}

/**
 * One logged set. Declared once, here: this existed as two separate `SessionExerciseRow` interfaces
 * that TypeScript quietly merged, so what a caller actually got depended on which fields happened to
 * be in scope.
 */
export interface SessionExerciseRow {
  id: string;
  element_index: number;
  /** 0-based set within the exercise. Rows logged before per-set existed carry 0. */
  set_index: number;
  status: "completed" | "skipped";
  rpe: number | null;
  reps: number | null;
  weight: number | null;
  duration_seconds: number | null;
  weight_unit: "lb" | "kg" | "bodyweight" | null;
  notes: string | null;
  flagged: boolean;
  flag_note: string | null;
}

export interface SessionHistoryRow {
  id: string;
  /**
   * How hard the athlete said the whole session felt, 1-10. The server has always returned it; the
   * history row never rendered it, so the one number that makes two sessions comparable at a glance was
   * invisible until you opened the record (UX review F79).
   */
  session_rpe?: number | null;
  plan_id: string;
  client_id: string;
  logged_by_role: "guardian" | "athlete";
  issue_flagged: boolean;
  completed_at: string;
  /** How they said they were going in. Null or absent for sessions logged before check-ins existed. */
  checkin?: StoredCheckin | null;
  /** Only the flagged ones -- the full exercise list is not carried on the history row. */
  flagged_exercises: SessionExerciseRow[];
}

/**
 * The per-set detail of one session.
 *
 * Only fetched when a history row is actually expanded: a client with months of history would otherwise
 * fan out one request per session on every page load, for detail nobody asked to see. The session's own
 * page uses `useSessionDetail` instead, which brings the prescription along with it.
 */
export function useSessionExercises(sessionId: string | undefined, enabled: boolean): UseQueryResult<SessionExerciseRow[]> {
  return useQuery({
    queryKey: queryKeys.sessionExercises(sessionId ?? ""),
    queryFn: () => apiGet<SessionExerciseRow[]>(`/sessions/${sessionId}/exercises`),
    enabled: Boolean(sessionId) && enabled,
  });
}

/** One set as it is held in a session in progress -- the same shape `POST /sessions` will receive. */
export interface DraftExercise {
  elementIndex: number;
  setIndex: number;
  status: "completed" | "skipped";
  reps: number | null;
  weight: number | null;
  weightUnit: "lb" | "kg" | "bodyweight" | null;
  durationSeconds: number | null;
  flagged: boolean;
  flagNote: string | null;
}

export interface SessionDraft {
  client_id: string;
  plan_id: string;
  current_index: number;
  current_set: number;
  exercises: DraftExercise[];
  /** The check-in answered before this workout began. A resumed workout keeps it rather than asking again. */
  checkin?: StoredCheckin | null;
  started_at: string;
  updated_at: string;
}

/**
 * The workout this athlete is part-way through, if any.
 *
 * A session used to exist only in the tab that started it, so a locked phone or a discarded background
 * tab threw away every set already done (UX review F70) -- and on iOS a backgrounded tab is routinely
 * evicted, which is precisely the case this product is used in.
 *
 * `staleTime: 0` deliberately: this is read on arriving at the walkthrough to decide whether to offer a
 * resume, and a cached "no session in progress" from before the phone locked is the exact wrong answer.
 */
export function useSessionDraft(clientId: string | undefined): UseQueryResult<{ draft: SessionDraft | null }> {
  return useQuery({
    queryKey: queryKeys.sessionDraft(clientId ?? ""),
    queryFn: () => apiGet<{ draft: SessionDraft | null }>(`/clients/${clientId}/session-draft`),
    enabled: Boolean(clientId),
    staleTime: 0,
  });
}

/**
 * The last few sessions in full, for "last time" during a workout.
 *
 * Read once when the workout opens, not per exercise. Defensive about the shape, because "last time"
 * is a nice-to-have on the workout screen and must never be the reason that screen fails.
 */
export function useExerciseHistory(clientId: string | undefined): UseQueryResult<RecentSessionSets[]> {
  return useQuery({
    queryKey: queryKeys.exerciseHistory(clientId ?? ""),
    queryFn: async () => {
      const response = await apiGet<{ sessions?: unknown }>(`/clients/${clientId}/exercise-history`);
      return Array.isArray(response?.sessions) ? (response.sessions as RecentSessionSets[]) : [];
    },
    enabled: Boolean(clientId),
  });
}

export function useSessions(clientId: string | undefined): UseQueryResult<SessionHistoryRow[]> {
  return useQuery({
    queryKey: queryKeys.sessions(clientId ?? ""),
    queryFn: () => apiGet<SessionHistoryRow[]>(`/clients/${clientId}/sessions`),
    enabled: Boolean(clientId),
  });
}

export interface SessionDetailResponse {
  session: SessionHistoryRow & { session_rpe: number | null; plan_title: string | null };
  exercises: SessionExerciseRow[];
  client: { id: string; name: string };
  plan: {
    title: string | null;
    elements: unknown;
    /** False when the prescription was read back from the live plan, so it may have changed since. */
    from_snapshot: boolean;
  };
}

/**
 * One session, whole: the prescription it was logged against alongside what was actually done.
 *
 * The history list could only ever say "8 reps" with nothing to compare it to -- whether that was the
 * whole prescription or half of it was invisible, which is most of what a coach reads a session for.
 */
export function useSessionDetail(sessionId: string | undefined): UseQueryResult<SessionDetailResponse> {
  return useQuery({
    queryKey: queryKeys.sessionDetail(sessionId ?? ""),
    queryFn: () => apiGet<SessionDetailResponse>(`/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
  });
}

export interface UnreadSummary {
  total: number;
  /** Every client whose row should show a dot. A family's private thread lights up all their athletes. */
  client_ids: string[];
}

/**
 * Whether anything is waiting, for the whole signed-in person (finding #14).
 *
 * Nothing anywhere said a message had arrived -- the only way to find out was to open each client's
 * messages page and look. Polled rather than pushed: the WebSocket only carries threads the open page
 * is watching, and the nav badge has to be right about conversations the user is nowhere near.
 */
export function useUnreadMessages(enabled = true): UseQueryResult<UnreadSummary> {
  return useQuery({
    queryKey: queryKeys.unreadMessages,
    queryFn: () => apiGet<UnreadSummary>("/messages/unread"),
    enabled,
    refetchInterval: 60_000,
    // A failed poll must not spawn an error state anywhere -- this drives a dot, not a page.
    retry: false,
  });
}

/** Clears this person's dot for one thread. Marks it read for them alone, never for anyone else. */
export function useMarkThreadRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => apiPost(`/threads/${threadId}/read`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.unreadMessages }),
  });
}

export interface FamilyBillingAthlete {
  client_id: string;
  name: string;
  plan_id: string | null;
  plan_name: string | null;
  plan_price_cents: number | null;
  /** A cheaper plan they have chosen, waiting for the month they have paid for to finish. */
  pending_plan_id: string | null;
  pending_plan_name: string | null;
  pending_effective_at: string | null;
  /** They have stopped training and are not on the invoice. */
  stopped?: boolean;
  /** A stop waiting for the month already paid for to finish. */
  stopping_at?: string | null;
  /** Which side stopped it -- and therefore which side can start it again. */
  stopped_by?: "coach" | "family" | null;
  /**
   * The plans this athlete's family may see and move to, as the coach chose them (27 Sep debug notes
   * item 12). Resolved by the server, so an athlete added before the choice existed lists every plan.
   * Absent on an older response, which reads the same way.
   */
  offered_plan_ids?: string[];
  /** The family's latest request to move onto a plan that needs the coach's approval. */
  plan_request?: PlanRequestSummary | null;
}

export interface PlanRequestSummary {
  plan_id: string;
  plan_name: string | null;
  requested_at: string;
  status: "pending" | "approved" | "declined";
  decided_at: string | null;
  /** Set when nobody decided it as such -- e.g. "That plan is no longer offered". */
  reason: string | null;
  family_seen: boolean;
}

/** Something a family did to their own billing that the coach should hear about. */
export interface BillingNotice {
  id: string;
  client_id: string;
  kind: "switched" | "cancelled";
  plan_name: string | null;
  effective_at: string | null;
  created_at: string;
}

export interface FamilyBillingSummary {
  athletes: FamilyBillingAthlete[];
  monthlyTotalCents: number | null;
  currency: string | null;
  siblingDiscountPercent: number | null;
}

/**
 * What this family is actually billed for, itemised (finding #15).
 *
 * Read by the family on their own settings page. A charge that first appears on an invoice is a support
 * ticket; the same number shown before it is charged is just the price.
 */
export function useFamilyBilling(clientId?: string, enabled = true): UseQueryResult<FamilyBillingSummary> {
  return useQuery({
    // Off for an athlete's own login, which has no billing to see and would only collect a 403.
    enabled,
    queryKey: queryKeys.familyBilling(clientId ?? "me"),
    // A family asks about themselves; a coach names the athlete, because the guardian's account id is
    // internal and never appears anywhere they can see.
    queryFn: () =>
      apiGet<FamilyBillingSummary>(clientId ? `/billing/family-summary?clientId=${clientId}` : "/billing/family-summary"),
    retry: false,
  });
}



/** Stops or resumes coaching one athlete. The coach's decision, and it changes the family's invoice. */
export function useCoachingState(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: "stop" | "resume") => apiPost(`/billing/athletes/${clientId}/${action}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["billing"] }).then(() =>
      queryClient.invalidateQueries({ queryKey: queryKeys.coachOverview })
    ),
  });
}

export function useThreads<T>(clientId: string | undefined): UseQueryResult<T> {
  return useQuery({
    queryKey: queryKeys.threads(clientId ?? ""),
    queryFn: () => apiGet<T>(`/clients/${clientId}/threads`),
    enabled: Boolean(clientId),
  });
}

export function useMessages(threadId: string | null): UseQueryResult<ThreadMessage[]> {
  return useQuery({
    queryKey: queryKeys.messages(threadId ?? ""),
    queryFn: () => apiGet<ThreadMessage[]>(`/threads/${threadId}/messages`),
    enabled: Boolean(threadId),
  });
}

/**
 * Invalidating a list after a write is the whole reason mutations live here rather than as bare
 * apiPost calls in components: the previous hand-rolled pages had to remember to call a `loadX()`
 * refetch by hand after every write, and a missed call meant stale UI with no error to notice.
 */
export function useInviteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost("/invites", body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.coachOverview }),
  });
}

export function useInviteActionMutation(action: "resend" | "cancel") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => apiPost(`/invites/${inviteId}/${action}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.coachOverview }),
  });
}

export function useLibrary<T>(search: string, tag: string): UseQueryResult<T> {
  return useQuery({
    queryKey: queryKeys.library(search, tag),
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (tag !== "all") params.set("tag", tag);
      const query = params.toString();
      return apiGet<T>(`/library${query ? `?${query}` : ""}`);
    },
    // Keeps the previous list on screen while a new search/filter is in flight, so typing in the
    // search box doesn't blank the results on every keystroke.
    placeholderData: (prev) => prev,
  });
}

export function useLibraryEntry<T>(entryId: string | undefined): UseQueryResult<T> {
  return useQuery({
    queryKey: queryKeys.libraryEntry(entryId ?? ""),
    queryFn: () => apiGet<T>(`/library/${entryId}`),
    enabled: Boolean(entryId),
  });
}

export function usePlanHistory<T>(clientId: string | undefined): UseQueryResult<T> {
  return useQuery({
    queryKey: queryKeys.planHistory(clientId ?? ""),
    queryFn: () => apiGet<T>(`/clients/${clientId}/plans/history`),
    enabled: Boolean(clientId),
  });
}

export function useCurrentPlan<T>(clientId: string | undefined): UseQueryResult<T> {
  return useQuery({
    queryKey: queryKeys.currentPlan(clientId ?? ""),
    queryFn: () => apiGet<T>(`/clients/${clientId}/plans/current`),
    enabled: Boolean(clientId),
    // "No plan published yet" is a legitimate empty state, not a transient failure -- retrying a 404
    // just delays showing the athlete the message they need to see.
    retry: false,
  });
}

/**
 * A client's goals (27 Sep debug notes item 8). Client-scoped, not plan-scoped -- a goal set against
 * whichever plan was live when it was written used to vanish from view the moment the next plan was
 * published. `/plans/{planId}/goals` still exists for goals written before this change; nothing here
 * reads it any more.
 */
export function useGoals<T>(clientId: string | undefined): UseQueryResult<T> {
  return useQuery({
    queryKey: queryKeys.goals(clientId ?? ""),
    queryFn: () => apiGet<T>(`/clients/${clientId}/goals`),
    enabled: Boolean(clientId),
  });
}

/**
 * The coach's running notes on a client -- a chart that belongs to the person, not to a plan.
 *
 * These were per-plan, so a note written in January disappeared from view the moment February's plan
 * was published, and "what do I know about this athlete" meant opening every plan in the history. The
 * plan-scoped endpoints still exist for the records already written against them; nothing in the UI
 * reads or writes them any more.
 */
export function useClientNotes<T>(clientId: string | undefined): UseQueryResult<T> {
  return useQuery({
    queryKey: queryKeys.clientNotes(clientId ?? ""),
    queryFn: () => apiGet<T>(`/clients/${clientId}/notes`),
    enabled: Boolean(clientId),
  });
}

export function useAddClientNote(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => apiPost(`/clients/${clientId}/notes`, { note }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.clientNotes(clientId) }),
  });
}

export function useDeleteClientNote(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => apiDelete(`/clients/${clientId}/notes/${noteId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.clientNotes(clientId) }),
  });
}

/**
 * Adds an athlete to a family already on the coach's roster (finding #15).
 *
 * This replaced the guardian's own "add a child": who trains with the coach is the coach's decision,
 * and each additional athlete is another subscription -- not something to start from a form on a
 * family's own dashboard.
 */
export function useAddClientToFamily() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      guardianEmail: string;
      name: string;
      age?: number;
      level?: string;
      startingPlanId?: string;
      offeredPlanIds?: string[];
    }) => apiPost("/coach/clients", input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.coachOverview }),
  });
}

/**
 * Creates a minor's own login. The endpoint was reachable only from the signup flow's final step, so a
 * guardian who skipped it there could never come back and do it later.
 *
 * Invalidates the client list because this flips the guardian's own access to that profile from editable
 * to read-only -- the state machine is live, not a one-time fork (architecture §2c).
 */
export function useCreateSkaterLogin(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { username: string; password: string }) =>
      apiPost(`/clients/${clientId}/login`, credentials),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.clients }),
  });
}

/**
 * Guardian-initiated password reset for a minor (PRD §11.2).
 *
 * Invalidates the client list on success so the "last reset" timestamp the guardian just created is
 * what they see, rather than the previous one.
 */
export function useResetSkaterPassword(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { password: string }) => apiPost(`/clients/${clientId}/login/reset-password`, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.clients }),
  });
}

/**
 * Whether this account may use the app, and what is missing if not (see lambda/account/status.ts).
 *
 * One request rather than stitching /consent and /billing/status together on every page load.
 */
export interface AccountStatus {
  role: "coach" | "family";
  /**
   * `not_applicable` is a minor's own login, which has no account of its own and never will. It used to
   * be a 403, which made the athlete's own pages fail open on an error rather than render on a fact --
   * see the server-side note in lambda/account/status.ts (UX review F53).
   */
  access: "ok" | "consent_required" | "billing_required" | "subscription_inactive" | "not_applicable";
  /**
   * The name families see -- on invite emails, the signup landing page, and beside the coach's messages.
   *
   * Read from the account item, which is the store those surfaces use. The settings page used to show
   * the Cognito `name` claim instead, so a coach could hold two different names at once with no way to
   * change either.
   */
  display_name?: string | null;
  billing_status: string | null;
  consent_complete: boolean;
  /** Preferred unit for entering weights. Only a default -- each logged set stores its own unit. */
  weight_unit: "lb" | "kg";
}

export function useAccountStatus(): UseQueryResult<AccountStatus> {
  return useQuery({
    queryKey: queryKeys.accountStatus,
    queryFn: () => apiGet<AccountStatus>("/account/status"),
    // A minor's own login gets a 403 here by design; retrying it just delays the page.
    retry: false,
  });
}

/** The account's default unit for entering weights. Each logged set still records its own. */
export function useSetWeightUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (weightUnit: "lb" | "kg") => apiPatch("/account/preferences", { weightUnit }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.accountStatus }),
  });
}

export function useConsent<T>(): UseQueryResult<T> {
  return useQuery({ queryKey: queryKeys.consent, queryFn: () => apiGet<T>("/consent") });
}

export function useBillingStatus<T>(): UseQueryResult<T> {
  return useQuery({ queryKey: queryKeys.billingStatus, queryFn: () => apiGet<T>("/billing/status") });
}

export { apiGet, apiPost, apiPatch };
